import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/apiClient";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Loader2, UserCheck, CheckSquare, Square } from "lucide-react";

export default function AssignStaffDialog({ consumer, staff, open, onOpenChange, onSaved }) {
  const [picked, setPicked] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (consumer) setPicked(consumer.assigned_staff || []);
  }, [consumer]);

  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/consumers/${consumer.id}/assign`, { staff_ids: picked });
      toast.success(`${consumer.name} assigned to ${picked.length} staff.`);
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="assign-staff-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">Assign {consumer?.name}</DialogTitle>
          <DialogDescription>Tap every staff member who works with this consumer. They'll see them on their dashboard.</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5 max-h-80 overflow-y-auto mt-1">
          {staff.length === 0 && <p className="text-sm text-[#7A8C82]">No PP/AP/QP staff accounts yet.</p>}
          {staff.map((s) => {
            const on = picked.includes(s.id);
            return (
              <button key={s.id} type="button" data-testid={`assign-staff-${s.id}`} onClick={() => toggle(s.id)}
                className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl border text-left transition-colors ${on ? "bg-[#EAF1EC] border-[#4A6B53]" : "bg-white border-[#E2DFD8] hover:border-[#4A6B53]"}`}>
                {on ? <CheckSquare className="w-5 h-5 text-[#4A6B53] shrink-0" strokeWidth={1.5} /> : <Square className="w-5 h-5 text-[#B8C4BC] shrink-0" strokeWidth={1.5} />}
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-semibold text-[#121814] truncate">{s.name}</span>
                  <span className="block text-xs text-[#7A8C82]">{s.credentials || s.role.toUpperCase()} · {s.assigned_count} consumer{s.assigned_count === 1 ? "" : "s"}</span>
                </span>
                <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-[#F4F6F3] text-[#4A6B53]">{s.role}</span>
              </button>
            );
          })}
        </div>
        <DialogFooter>
          <Button data-testid="confirm-assign-btn" onClick={save} disabled={saving} className="rounded-full bg-[#4A6B53] hover:bg-[#3D5A45] text-white">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserCheck className="w-4 h-4 mr-2" strokeWidth={1.5} />} Save assignment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
